import { skipToken } from '@reduxjs/toolkit/query/react';
import { useGetCurrentUserQuery } from '@/features/user/api/userApi';
import { useAuthState } from '@/auth/authState';
import type { CurrentUserDto } from '@/types';

export interface Identity {
  user: CurrentUserDto | undefined;
  isLoading: boolean;
  /** True while /me is (re)fetching, e.g. after the User tag is invalidated. */
  isFetching: boolean;
  /**
   * True before auth is ready (/me is skipped) or before the query has started —
   * i.e. /me has NOT settled. RTK reports isLoading/isFetching=false for a skipped
   * query, so consumers must use this to tell "not asked yet" from "settled, free".
   */
  isUninitialized: boolean;
  isError: boolean;
}

/** Single source of truth for the logged-in identity, from GET /api/v1/me. */
export function useIdentity(): Identity {
  // Gate the request on auth being ready (token in place). Firing it earlier —
  // e.g. on the /auth/callback route, before the OIDC code→token exchange
  // completes — sends /me without an Authorization bearer, yielding a 401 and a
  // bounce back to the login page. See COPA-221.
  const { isAuthenticated, isLoading: authLoading } = useAuthState();
  const ready = isAuthenticated && !authLoading;
  const { data, isLoading, isFetching, isUninitialized, isError } = useGetCurrentUserQuery(
    ready ? undefined : skipToken,
  );
  // While auth is not ready the /me query is skipped, so RTK reports
  // isLoading/isFetching=false; fold that pre-auth window into isUninitialized so
  // consumers don't mistake "not asked yet" for "settled, no workflow".
  return { user: data, isLoading, isFetching, isUninitialized: !ready || isUninitialized, isError };
}
